import "./CronJobs.css";
import "../users/MasterUser.css";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { Column } from "primereact/column";
import { ThreeDots } from "react-loader-spinner";
import { DataTable } from "primereact/datatable";
import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import axios from "axios";
import moment from "moment-timezone";
import addicon from "../../../images/icons/addicon.svg";
import crssrImage from "../../../images/singleinvoice/cross-red.svg";
import { setHeaderText } from "../../../features/headerText/headerTextSlice";

const fadeIn = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: { opacity: 1, scale: 1 },
};

const CRON_PRESETS = [
  { label: "Every 15 minutes", value: "*/15 * * * *" },
  { label: "Every 30 minutes", value: "*/30 * * * *" },
  { label: "Every hour", value: "0 * * * *" },
  { label: "Every 5 hours", value: "0 */5 * * *" },
  { label: "Every day at midnight", value: "0 0 * * *" },
  { label: "Custom", value: "custom" },
];

const EMPTY_FORM = {
  name: "",
  subDomain: "",
  tenantName: "",
  description: "",
  cronPreset: "",
  cronExpression: "",
  endpointUrl: "",
  method: "GET",
  isActive: true,
};

const STATUS_COLORS = {
  success: { color: "#7CC273", bg: "#D5F3D8", border: "#7CC273" },
  error: { color: "#d9534f", bg: "#fde8e8", border: "#d9534f" },
  running: { color: "#f59e0b", bg: "#fef3c7", border: "#f59e0b" },
};

export default function CronJobsOverview() {
  const dispatch = useDispatch();
  const { masterUser } = useSelector((state) => state.masterAuth);
  const token = masterUser?.token || null;

  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [triggerLoadingId, setTriggerLoadingId] = useState(null);
  const [toggleLoadingId, setToggleLoadingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const triggerAllUrl = `${window.location.origin}/api/master/cron-jobs/trigger-all`;

  useEffect(() => {
    dispatch(setHeaderText("Cron Jobs"));
  }, [dispatch]);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get("/api/master/cron-jobs", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobs(res.data.results || []);
    } catch {
      toast.error("Failed to load cron jobs", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await axios.get("/api/master/cron-jobs/tenants", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants(res.data.results || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchJobs();
    fetchTenants();
  }, [fetchJobs, fetchTenants]);

  const openAdd = () => {
    setEditingJob(null);
    setForm(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const openEdit = (job) => {
    setEditingJob(job);
    const preset = CRON_PRESETS.find((p) => p.value === job.cronExpression && p.value !== "custom");
    setForm({
      name: job.name || "",
      subDomain: job.subDomain || "",
      tenantName: job.tenantName || "",
      description: job.description || "",
      cronPreset: preset ? preset.value : "custom",
      cronExpression: job.cronExpression || "",
      endpointUrl: job.endpointUrl || "",
      method: job.method || "GET",
      isActive: job.isActive !== false,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handlePresetChange = (val) => {
    if (val === "custom") {
      setForm((f) => ({ ...f, cronPreset: "custom", cronExpression: "" }));
    } else {
      setForm((f) => ({ ...f, cronPreset: val, cronExpression: val }));
    }
  };

  const handleTenantSelect = (subDomain) => {
    if (subDomain === "__custom__") {
      setForm((f) => ({ ...f, subDomain: "", tenantName: "" }));
      return;
    }
    const t = tenants.find((x) => x.subDomain === subDomain);
    setForm((f) => ({
      ...f,
      subDomain,
      tenantName: t ? (t.commercial_name || t.ownerName || "") : f.tenantName,
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subDomain.trim() || !form.cronExpression.trim() || !form.endpointUrl.trim()) {
      toast.error("Name, subdomain, cron expression, and endpoint URL are required", {
        position: "bottom-right", autoClose: 2000,
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name, subDomain: form.subDomain, tenantName: form.tenantName,
        description: form.description, cronExpression: form.cronExpression,
        endpointUrl: form.endpointUrl, method: form.method, isActive: form.isActive,
      };
      if (editingJob) {
        await axios.put(`/api/master/cron-jobs/${editingJob._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Cron job updated", { position: "bottom-right", autoClose: 2000 });
      } else {
        await axios.post("/api/master/cron-jobs", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Cron job created", { position: "bottom-right", autoClose: 2000 });
      }
      setIsModalOpen(false);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (job) => {
    setToggleLoadingId(job._id);
    try {
      await axios.put(`/api/master/cron-jobs/${job._id}`, { isActive: !job.isActive }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(job.isActive ? "Cron job stopped" : "Cron job activated", {
        position: "bottom-right", autoClose: 1500,
      });
      fetchJobs();
    } catch {
      toast.error("Toggle failed", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setToggleLoadingId(null);
    }
  };

  const handleTrigger = async (job) => {
    setTriggerLoadingId(job._id);
    try {
      await axios.post(`/api/master/cron-jobs/${job._id}/trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Triggered: ${job.name}`, { position: "bottom-right", autoClose: 2000 });
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Trigger failed", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setTriggerLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    setDeleteLoadingId(id);
    try {
      await axios.delete(`/api/master/cron-jobs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Cron job deleted", { position: "bottom-right", autoClose: 1500 });
      setDeleteConfirmId(null);
      fetchJobs();
    } catch {
      toast.error("Delete failed", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const copyTriggerUrl = () => {
    navigator.clipboard.writeText(triggerAllUrl);
    toast.success("Trigger URL copied!", { position: "bottom-right", autoClose: 1500 });
  };

  // ── Column body renderers ──────────────────────────────────────────────────

  const nameBody = (row) => (
    <div>
      <div style={{ fontWeight: 500, color: "#282828" }}>{row.name}</div>
      {row.description && (
        <div style={{ fontSize: "12px", color: "#6A7988", marginTop: "2px" }}>{row.description}</div>
      )}
    </div>
  );

  const tenantBody = (row) => (
    <div>
      <div style={{ fontWeight: 500, color: "#282828" }}>{row.tenantName || row.subDomain}</div>
      <div style={{ fontSize: "12px", color: "#6A7988" }}>{row.subDomain}</div>
    </div>
  );

  const scheduleBody = (row) => (
    <code style={{ background: "#F5F6F8", borderRadius: "4px", padding: "3px 8px", fontSize: "13px", color: "#405BFF" }}>
      {row.cronExpression}
    </code>
  );

  const endpointBody = (row) => (
    <div style={{ fontSize: "13px", color: "#454F59", wordBreak: "break-all" }}>
      <span style={{ fontSize: "11px", background: "#F5F6F8", borderRadius: "4px", padding: "1px 5px", color: "#6A7988", marginRight: "5px" }}>
        {row.method || "GET"}
      </span>
      {row.endpointUrl}
    </div>
  );

  const lastRunBody = (row) => {
    if (!row.lastTriggeredAt) return <span style={{ color: "#96A8BA", fontSize: "14px" }}>Never</span>;
    return (
      <div>
        <div style={{ fontSize: "14px", color: "#282828" }}>{moment(row.lastTriggeredAt).format("MMM D, YYYY HH:mm")}</div>
        <div style={{ fontSize: "12px", color: "#6A7988" }}>{moment(row.lastTriggeredAt).fromNow()}</div>
      </div>
    );
  };

  const lastStatusBody = (row) => {
    if (!row.lastTriggerStatus) return <span style={{ color: "#96A8BA" }}>—</span>;
    const s = STATUS_COLORS[row.lastTriggerStatus] || { color: "#96A8BA", bg: "#EAEAEA", border: "#6A7988" };
    return (
      <span className="payment-status" style={{
        color: s.color, background: s.bg, border: `0.2px solid ${s.border}`,
        borderRadius: "100px", padding: "0 12px", height: "31px", display: "inline-flex",
        alignItems: "center", fontSize: "14px", fontWeight: 500, whiteSpace: "nowrap",
      }}>
        {row.lastTriggerStatus.charAt(0).toUpperCase() + row.lastTriggerStatus.slice(1)}
      </span>
    );
  };

  const activeStatusBody = (row) => {
    const isActive = row.isActive !== false;
    return (
      <span className={`payment-status ${isActive ? "active" : "inactive"}`}>
        {isActive ? "Active" : "Stopped"}
      </span>
    );
  };

  const actionsBody = (row) => (
    <div className="cj-actions-cell">
      <button
        className="cj-action-btn cj-run-btn"
        onClick={() => handleTrigger(row)}
        disabled={triggerLoadingId === row._id}
        title="Run now"
      >
        {triggerLoadingId === row._id ? (
          <ThreeDots height="12" width="20" radius="6" color="#fff" visible={true} />
        ) : "Run"}
      </button>
      <button className="cj-action-btn cj-edit-btn" onClick={() => openEdit(row)} title="Edit">
        Edit
      </button>
      <button
        className={`cj-action-btn ${row.isActive ? "cj-stop-btn" : "cj-activate-btn"}`}
        onClick={() => handleToggle(row)}
        disabled={toggleLoadingId === row._id}
      >
        {toggleLoadingId === row._id ? (
          <ThreeDots height="12" width="20" radius="6" color="#fff" visible={true} />
        ) : row.isActive ? "Stop" : "Activate"}
      </button>
      {deleteConfirmId === row._id ? (
        <>
          <button
            className="cj-action-btn cj-confirm-btn"
            onClick={() => handleDelete(row._id)}
            disabled={deleteLoadingId === row._id}
          >
            {deleteLoadingId === row._id ? <ThreeDots height="12" width="20" radius="6" color="#fff" visible={true} /> : "Confirm"}
          </button>
          <button className="cj-action-btn cj-cancel-inline-btn" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </button>
        </>
      ) : (
        <button className="cj-action-btn cj-delete-btn" onClick={() => setDeleteConfirmId(row._id)} title="Delete">
          Delete
        </button>
      )}
    </div>
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      transition={{ duration: 0.18 }}
    >
      <div className="user-outer-overview invoice-outer-invoice">

        {/* Top action bar */}
        <div className="invoice-top">
          <div className="new-top-flter">
            <div className="button-sec-invoice">
              <a className="btn-forpop-initem" onClick={openAdd} style={{ cursor: "pointer" }}>
                <span className="icon-invoice">
                  <img src={addicon} alt="Add Cron Job" />
                </span>
                Add Cron Job
              </a>
            </div>
          </div>
        </div>

        {/* External trigger URL banner */}
        <div className="cj-trigger-banner">
          <div className="cj-trigger-label">
            External Trigger URL
            <span className="cj-trigger-hint"> — runs all active cron jobs when called</span>
          </div>
          <div className="cj-trigger-url-row">
            <code className="cj-trigger-url">{triggerAllUrl}</code>
            <button className="cj-copy-btn" onClick={copyTriggerUrl}>Copy URL</button>
          </div>
          <div className="cj-trigger-note">
            Optional: set <code>CRON_TRIGGER_SECRET</code> in server env and pass <code>x-cron-secret</code> header for protection.
          </div>
        </div>

        {/* Table */}
        <div className="card-outer1">
          <div className="card-outer">
            <div className="card">
              {isLoading === true ? (
                <div className="outter-load-table">
                  <ThreeDots
                    height="80"
                    width="80"
                    radius="9"
                    color="#405BFF"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClassName=""
                    visible={true}
                  />
                </div>
              ) : (
                <DataTable
                  value={jobs}
                  first={first}
                  rows={rows}
                  removableSort
                  paginator={true}
                  paginatorPosition="bottom"
                  totalRecords={jobs.length}
                  onPage={(e) => setFirst(e.first)}
                  emptyMessage="No cron jobs found. Click 'Add Cron Job' to create one."
                  tableStyle={{ minWidth: "50rem", width: "100%", textAlign: "left" }}
                  paginatorClassName="recent-invoices-pagination"
                >
                  <Column header="Name" body={nameBody} style={{ paddingRight: "44px" }} />
                  <Column header="Tenant" body={tenantBody} style={{ paddingRight: "44px" }} />
                  <Column header="Schedule" body={scheduleBody} style={{ paddingRight: "44px" }} />
                  <Column header="Endpoint" body={endpointBody} style={{ paddingRight: "44px" }} />
                  <Column header="Last Run" body={lastRunBody} style={{ paddingRight: "44px" }} />
                  <Column header="Last Status" body={lastStatusBody} style={{ paddingRight: "44px" }} />
                  <Column header="Status" body={activeStatusBody} style={{ paddingRight: "44px" }} />
                  <Column header="Actions" body={actionsBody} style={{ paddingRight: "8px" }} />
                </DataTable>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Add / Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={!saving ? handleCloseModal : undefined}
        contentLabel={editingJob ? "Edit Cron Job" : "Add Cron Job"}
        className="user-invite-modal-main-outer cj-modal-outer"
        style={{
          content: {
            height: "auto",
            maxHeight: "90vh",
            width: "520px",
            position: "fixed",
            top: "50%",
            left: "50%",
          },
        }}
      >
        <div className="modal">
          <div className="modal-top-section-user-invite">
            <div className="modal-invite-head">
              <h2>{editingJob ? "Edit Cron Job" : "Add New Cron Job"}</h2>
            </div>
            <div className="modal-close-invite outter-modal-main outter-modal-main-user">
              <a onClick={handleCloseModal}>
                <img src={crssrImage} alt="Close" />
              </a>
            </div>
          </div>

          <div className="the-modal-inuser">
            <div className="cj-field-group">
              <label>Job Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Threshold Notify - tenantX"
                className="cj-input"
              />
            </div>

            <div className="cj-field-row">
              <div className="cj-field-group">
                <label>Tenant *</label>
                <select
                  value={form.subDomain}
                  onChange={(e) => handleTenantSelect(e.target.value)}
                  className="cj-input"
                >
                  <option value="">Select tenant...</option>
                  {tenants.map((t) => (
                    <option key={t.subDomain} value={t.subDomain}>
                      {t.commercial_name || t.ownerName} ({t.subDomain})
                    </option>
                  ))}
                  <option value="__custom__">— Enter manually —</option>
                </select>
                {form.subDomain === "" && (
                  <input
                    type="text"
                    style={{ marginTop: "6px" }}
                    placeholder="Subdomain (manual)"
                    className="cj-input"
                    onChange={(e) => setForm((f) => ({ ...f, subDomain: e.target.value }))}
                  />
                )}
              </div>
              <div className="cj-field-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={form.tenantName}
                  onChange={(e) => setForm((f) => ({ ...f, tenantName: e.target.value }))}
                  placeholder="Auto-filled"
                  className="cj-input"
                />
              </div>
            </div>

            <div className="cj-field-group">
              <label>Schedule *</label>
              <select value={form.cronPreset} onChange={(e) => handlePresetChange(e.target.value)} className="cj-input">
                <option value="">Select preset...</option>
                {CRON_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              {(form.cronPreset === "custom" || (form.cronPreset && !CRON_PRESETS.find((p) => p.value === form.cronPreset && p.value !== "custom"))) && (
                <input
                  type="text"
                  style={{ marginTop: "6px" }}
                  value={form.cronExpression}
                  onChange={(e) => setForm((f) => ({ ...f, cronExpression: e.target.value }))}
                  placeholder="Cron expression e.g. 0 */5 * * *"
                  className="cj-input"
                />
              )}
              {form.cronExpression && (
                <div style={{ fontSize: "12px", color: "#6A7988", marginTop: "4px" }}>
                  Expression: <code>{form.cronExpression}</code>
                </div>
              )}
            </div>

            <div className="cj-field-row">
              <div className="cj-field-group" style={{ flex: 3 }}>
                <label>Endpoint URL *</label>
                <input
                  type="text"
                  value={form.endpointUrl}
                  onChange={(e) => setForm((f) => ({ ...f, endpointUrl: e.target.value }))}
                  placeholder="https://subdomain.talenton.app/api/..."
                  className="cj-input"
                />
              </div>
              <div className="cj-field-group" style={{ flex: 1 }}>
                <label>Method</label>
                <select value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))} className="cj-input">
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                </select>
              </div>
            </div>

            <div className="cj-field-group">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional notes about this cron job"
                rows={2}
                className="cj-input"
              />
            </div>

            <div className="cj-field-group" style={{ flexDirection: "row", alignItems: "center", gap: "10px", marginTop: "8px" }}>
              <input
                type="checkbox"
                id="cj-active"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
              />
              <label htmlFor="cj-active" style={{ cursor: "pointer", marginBottom: 0, fontWeight: 400, color: "#454F59" }}>
                Active (included in trigger-all runs)
              </label>
            </div>
          </div>

          <div className="sprint-btn-outer">
            <button
              type="button"
              className="submit-client cancel-sprint"
              onClick={handleCloseModal}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="submit-client save-sprint"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ThreeDots height="18" width="20" radius="9" color="white" visible={true} />
              ) : editingJob ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
